import { ColorShade } from '@shinetools/sunshine';
import { Color, ColorVariant } from '@shinetools/suntheme';
import { History } from 'history';

import { CreditCard } from '__generated__/GQL';
import { isCreditCardPendingOrder } from 'common/creditCards/isCreditCardPendingOrder';
import {
  isNewRebrandedCardDesign,
  isOldCardDesign,
} from 'common/paymentCards/utils';
import { CreditCardStatus } from 'types/creditCard';

import {
  CardActionDetails,
  CardActionProps,
  formatActionDetailsToActionCell,
} from '../../utils/cardActions';
import * as actions from './cardActions';
import locales from './locales';

export const getCardPanStyle = (
  creditCard: CreditCard,
): { color: Color; colorVariant: ColorVariant | ColorShade } => {
  const { isPhysical, isPremium } = creditCard;

  if (!isPhysical) {
    return { color: 'grey', colorVariant: 800 };
  }

  if (isNewRebrandedCardDesign(creditCard)) {
    return isPremium
      ? { color: 'grey', colorVariant: 100 }
      : { color: 'grey', colorVariant: 800 };
  }

  const isOldDesign = isOldCardDesign(creditCard);
  if (isOldDesign) {
    return { color: 'white', colorVariant: 0 };
  }
  return { color: 'lightBlue', colorVariant: isPremium ? 2 : 0 };
};

export const getCardLabelStyle = (
  creditCard: CreditCard,
): { color: Color; colorVariant: ColorVariant | ColorShade } => {
  const { isPhysical, isPremium } = creditCard;

  if (!isPhysical) {
    return { color: 'grey', colorVariant: 700 };
  }

  if (isNewRebrandedCardDesign(creditCard)) {
    return isPremium
      ? { color: 'grey', colorVariant: 500 }
      : { color: 'grey', colorVariant: 700 };
  }

  const isOldDesign = isOldCardDesign(creditCard);
  if (isOldDesign) {
    return { color: 'grey', colorVariant: 1 };
  }
  return { color: isPremium ? 'grey' : 'lightBlue', colorVariant: 1 };
};

interface GetActionsForPhysicalCardParams {
  isOwner: boolean;
  status: CreditCardStatus;
  pinTryExceeds: boolean;
  canRequestPaymentCards: boolean;
  canOrderPaymentCards: boolean;
}

function getActionsForPhysicalCard({
  canOrderPaymentCards,
  canRequestPaymentCards,
  isOwner,
  pinTryExceeds,
  status,
}: GetActionsForPhysicalCardParams): CardActionDetails[] {
  switch (status) {
    case 'ACTIVE':
      return pinTryExceeds
        ? [actions.UNBLOCK_PIN]
        : [actions.LOCK_OPTIONS, ...(isOwner ? [actions.UPDATE_PIN] : [])];
    case 'INACTIVE':
      return isOwner ? [actions.ACTIVATE, actions.UPDATE_PIN] : [];
    case 'LOCKED':
      return [
        ...(pinTryExceeds ? [actions.UNBLOCK_PIN] : [actions.UNLOCK]),
        actions.MARK_AS_STOLEN,
        actions.MARK_AS_LOST,
      ];
    case 'LOST':
    case 'STOLEN':
      if (isOwner) {
        if (canOrderPaymentCards) {
          return [actions.ORDER_NEW_PHYSICAL_CARD];
        }
        if (canRequestPaymentCards) {
          return [actions.REQUEST_NEW_PHYSICAL_CARD];
        }
      }
      return [];
    default:
      return [];
  }
}

const LOCK_OR_DELETE_VIRTUAL_CARD_ACTION: CardActionDetails = {
  ...actions.LOCK_OPTIONS,
  title: locales.cardActions.lockOrDeleteTitle,
};

interface GetActionsForVirtualCardParams {
  status: CreditCardStatus;
}

function getActionsForVirtualCard({
  status,
}: GetActionsForVirtualCardParams): CardActionDetails[] {
  switch (status) {
    case 'ACTIVE':
      return [LOCK_OR_DELETE_VIRTUAL_CARD_ACTION];
    case 'LOCKED':
      return [actions.UNLOCK, actions.DELETE];
    default:
      return [];
  }
}

interface GetCardActionsParams {
  creditCard: CreditCard;
  isOwner: boolean;
  history: History;
  canRequestPaymentCards: boolean;
  canOrderPaymentCards: boolean;
}

export const getCardActions = ({
  canOrderPaymentCards,
  canRequestPaymentCards,
  creditCard,
  history,
  isOwner,
}: GetCardActionsParams): CardActionProps[] => {
  if (isCreditCardPendingOrder(creditCard)) {
    return [];
  }
  const { creditCardId, isPhysical, pinTryExceeds, status } = creditCard;
  const actionsList = isPhysical
    ? getActionsForPhysicalCard({
        canOrderPaymentCards,
        canRequestPaymentCards,
        isOwner,
        pinTryExceeds,
        status,
      })
    : getActionsForVirtualCard({ status });

  return formatActionDetailsToActionCell(actionsList, creditCardId, history);
};
